/* ==========================================================================
   dtc-popular.js, infinite loop, mouse drag and arrows for the Popular Now rail

   Pure enhancement. The rail ships as a real `overflow-x: auto` row, so
   swipe, trackpad, keyboard and screen-reader panning already work with this
   file absent, it just ends at the last shirt. What JS adds:

     loop     the collection repeats forever in both directions
     drag     grab-and-throw with the mouse, with a decaying fling
     arrows   two buttons, rendered `hidden` by Liquid because they mean
              nothing without JS

   THE LOOP
   --------
   The card list is cloned until the track is comfortably wider than the
   viewport, and scrollLeft is kept inside the window [period, 2*period).
   Shifting by exactly one period lands on a pixel-identical view, so the
   jump is invisible. See measurePeriod() for why that width is not simply
   read off the DOM.

   WHEN the shift happens is the whole trick. Writing scrollLeft during a
   native momentum scroll kills that momentum, on a phone that reads as the
   row snagging. So a wrap is only taken:

     - immediately, during a mouse drag or fling, because we own scrollLeft
       there and no native momentum is running;
     - otherwise once scrolling has SETTLED (scrollend, or a 150ms debounce);
     - immediately, if the row somehow reaches a physical end anyway.

   That is affordable because the track carries a full period of runway past
   each side of the window, a single fling can never reach the real end, so
   deferring the wrap is never visible.

   Clones are decorative: aria-hidden, ids stripped, controls taken out of the
   tab order, and mousedown defaulted away so focus can never land inside an
   aria-hidden subtree (Chrome answers that by stripping the aria-hidden and
   exposing every duplicate shirt to assistive tech). They stay clickable,
   the card under the cursor is usually a clone.

   The loop only engages when the real cards overflow the viewport. If the
   whole collection already fits there is nothing to loop, and repeating two
   shirts across a wide screen would just look broken.

   There is deliberately NO scroll-snap. An endless loop has no canonical
   position to snap to, so mandatory snap could only ever yank the row off
   wherever the visitor left it. The fling's own decay is the settle, the
   same way the Newest In ticker settles into its drift rather than into an
   index.

   No scroll hijacking: arrows are native scrollBy, the drag writes
   scrollLeft, and the browser owns the easing. Every listener, observer,
   timer and rAF is released on cleanup, the theme editor mounts and
   unmounts constantly.
   ========================================================================== */
(function () {
  'use strict';

  var DTC = window.DTC;
  if (!DTC || !DTC.register) return;

  var EPS = 2;          // px of slack, so sub-pixel scroll positions don't flap
  var SLOP = 6;         // px of travel before a press becomes a drag, not a click
  var FRICTION = 0.94;  // of the fling velocity survives each 16.67ms
  var MIN_VEL = 0.02;   // px/ms, below this the fling has stopped
  var MAX_VEL = 3;      // px/ms ceiling so a violent throw stays readable
  var VEL_DT = 4;       // ms, shorter samples than this are noise, not speed
  var MAX_FLING = 900;  // ms, hard stop, so no velocity can coast forever
  var SETTLE = 150;     // ms of quiet before a native scroll counts as finished

  DTC.register('popular', {
    init: function (root) {
      var vp = root.querySelector('[data-pop-viewport]');
      var track = root.querySelector('[data-pop-track]');
      var nav = root.querySelector('[data-pop-nav]');
      var prev = root.querySelector('[data-pop-prev]');
      var next = root.querySelector('[data-pop-next]');
      if (!vp || !track || !nav || !prev || !next) return;

      var originals = Array.prototype.slice.call(track.children);
      if (!originals.length) return;

      var offs = [];
      var ro = null;
      var queued = 0;
      var raf = 0;
      var settleTimer = 0;
      var drag = null;
      var eatClick = false;

      var clones = [];
      var period = 0;       // px covered by one full copy of the collection
      var looping = false;
      var wantsLoop = false;
      var lastW = -1;       // -1, not 0: 0 is a real (pre-layout) width
      var retries = 0;

      function maxScroll() { return vp.scrollWidth - vp.clientWidth; }

      /* ---------------------------------------------------------------- *
       * Clones + geometry
       * ---------------------------------------------------------------- */

      // Killing mousedown's default stops the browser focusing the control
      // and leaves the click that follows completely untouched.
      function preventFocus(e) { e.preventDefault(); }

      function appendCopy() {
        var set = [];
        for (var i = 0; i < originals.length; i++) {
          var c = originals[i].cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          c.setAttribute('data-pop-clone', '');

          // Duplicate ids would poison getElementById and every label/aria
          // reference on the page. The card markup ships none today; a
          // placeholder SVG or a future edit could.
          if (c.id) c.removeAttribute('id');
          var ided = c.querySelectorAll('[id]');
          for (var k = 0; k < ided.length; k++) ided[k].removeAttribute('id');

          var f = c.querySelectorAll('a[href], button');
          for (var j = 0; j < f.length; j++) {
            f[j].setAttribute('tabindex', '-1');
            f[j].addEventListener('mousedown', preventFocus);
          }

          track.appendChild(c);
          set.push(c);
        }
        return set;
      }

      function dropClones() {
        for (var i = 0; i < clones.length; i++) {
          if (clones[i].parentNode) clones[i].parentNode.removeChild(clones[i]);
        }
        clones = [];
        period = 0;
        looping = false;
      }

      /* One copy's width, and it has to be EXACT.
         The obvious measurement, the distance from a card to its own clone,
         is right in principle and is what makes the trailing gap fall out for
         free, but offsetLeft is integer-rounded while the real card pitch is
         fractional (a clamp() width beside a clamp() gap). Wrapping by the
         rounded figure leaves a fraction of a pixel behind on every cycle,
         and the seam creeps until it visibly stutters.

         So the pitch is taken from computed styles instead, which are
         fractional AND immune to the [data-sec] scale transform that may be
         on the section while this runs (getBoundingClientRect is not). Cards
         are flex: 0 0 <basis> with no padding or border, so every one is
         exactly that width, but the laid-out distance still gets the final
         say on whether that assumption held. */
      function measurePeriod(firstClone) {
        var laidOut = firstClone.offsetLeft - originals[0].offsetLeft;
        var w = parseFloat(getComputedStyle(originals[0]).width);
        var gap = parseFloat(getComputedStyle(track).columnGap);
        if (isNaN(gap)) gap = 0;
        var exact = originals.length * (w + gap);
        if (isFinite(exact) && exact > 0 && Math.abs(exact - laidOut) <= 1.5) return exact;
        return laidOut;
      }

      // The whole loop is sized from vp.clientWidth, so a measurement taken
      // before the row has been laid out silently produces too few copies,
      // and the shortfall is invisible until someone flings the row into the
      // bare end. Refuse to build on a zero-width viewport; the
      // ResizeObserver calls straight back once there is a real width.
      function build() {
        if (vp.clientWidth <= 0) return;

        var at = vp.scrollLeft;
        dropClones();
        lastW = vp.clientWidth;
        wantsLoop = false;

        // Nothing to loop if the real cards already fit, or if a lone card
        // would just repeat across the screen.
        if (originals.length < 2 || maxScroll() <= EPS) {
          vp.scrollLeft = Math.min(at, Math.max(0, maxScroll()));
          return;
        }
        wantsLoop = true;

        var first = appendCopy();
        clones = first.slice();
        period = measurePeriod(first[0]);
        if (period <= 0) { dropClones(); return; }

        // The window is [period, 2*period), so the track needs a whole spare
        // period of runway past each side of it. An extra copy is cheap; a
        // fling that hits the real end and stops dead is not.
        var extra = Math.ceil(vp.clientWidth / period) + 2;
        for (var n = 1; n < extra; n++) clones = clones.concat(appendCopy());

        looping = true;
        vp.scrollLeft = period + (at % period);
      }

      // The invariant the copy count exists to buy: a whole period of runway
      // past each side of the [period, 2*period) window. Checked directly
      // rather than trusting the arithmetic that produced it, so a build that
      // measured badly repairs itself instead of shipping a short track.
      function loopHealthy() {
        return looping && period > 0 && maxScroll() >= period * 3;
      }

      // Keeps scrollLeft inside [period, 2*period). Returns the shift applied,
      // so a live drag can keep its own origin in step.
      function wrapNow() {
        if (!looping || period <= 0) return 0;
        var shift = 0;
        var sl = vp.scrollLeft;
        while (sl < period) { sl += period; shift += period; }
        while (sl >= period * 2) { sl -= period; shift -= period; }
        if (shift) vp.scrollLeft = sl;
        return shift;
      }

      /* ---------------------------------------------------------------- *
       * Arrows
       * ---------------------------------------------------------------- */

      // One card plus one gap. Measured live rather than hard-coded: the card
      // width is a clamp() and the gap a clamp(), so both change with the
      // viewport and neither is knowable from here.
      function step() {
        var card = track.firstElementChild;
        if (!card) return vp.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap);
        if (isNaN(gap)) gap = 0;
        return card.getBoundingClientRect().width + gap;
      }

      function go(dir) {
        stopFling();
        vp.scrollBy({
          left: dir * step(),
          behavior: DTC.reducedMotion ? 'auto' : 'smooth'
        });
      }

      function sync() {
        queued = 0;

        // Card widths are clamped against the viewport, so a resize changes
        // `period` and the row has to be rebuilt around it. The second branch
        // is the repair path for a build that ran against a viewport which
        // had not been laid out yet. Both terminate: a rebuild leaves
        // clientWidth untouched, and the retry counter caps the repair.
        if (vp.clientWidth > 0) {
          if (vp.clientWidth !== lastW) { retries = 0; build(); }
          else if (wantsLoop && !loopHealthy() && retries < 3) { retries++; build(); }
        }

        var max = maxScroll();
        var scrollable = max > EPS;
        // Nothing to scroll to -> hand the arrows back to the hidden state
        // Liquid rendered them in, and drop the grab cursor.
        nav.hidden = !scrollable;
        root.classList.toggle('is-scrollable', scrollable);
        // While looping there is always more in both directions.
        prev.disabled = !looping && vp.scrollLeft <= EPS;
        next.disabled = !looping && vp.scrollLeft >= max - EPS;
      }

      // Scroll fires per frame while a swipe or fling is in flight; sync()
      // reads layout, so coalesce it onto one rAF rather than per event.
      function schedule() {
        if (queued) return;
        queued = requestAnimationFrame(sync);
      }

      function onScroll() {
        schedule();
        if (!looping) return;
        // Safety valve: never let a native fling actually reach the end.
        if (vp.scrollLeft <= 0 || vp.scrollLeft >= maxScroll()) { wrapNow(); return; }
        // Otherwise defer, so native momentum is never interrupted.
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          settleTimer = 0;
          if (!drag && !raf) wrapNow();
        }, SETTLE);
      }

      /* ---------------------------------------------------------------- *
       * Drag
       * ---------------------------------------------------------------- */

      function stopFling() {
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
      }

      // The row keeps whatever position it was left in, there is nothing to
      // snap back to. This only drops the grabbing cursor and re-enables the
      // card transitions.
      function endDrag() {
        root.classList.remove('is-dragging');
      }

      function fling(v0) {
        var v = Math.max(-MAX_VEL, Math.min(MAX_VEL, v0));
        var last = 0;
        var began = 0;
        function tick(t) {
          if (!last) { last = t; began = t; raf = requestAnimationFrame(tick); return; }
          var dt = t - last;
          last = t;
          v *= Math.pow(FRICTION, dt / 16.67);
          // Drag right (positive velocity) reveals earlier cards, i.e.
          // scrollLeft decreases, hence the minus.
          vp.scrollLeft -= v * dt;
          wrapNow();
          var atEnd = !looping && (vp.scrollLeft <= 0 || vp.scrollLeft >= maxScroll());
          // MAX_FLING is the backstop: friction alone always converges, but a
          // wall-clock bound means no input, however pathological, can leave
          // the row coasting indefinitely.
          if (Math.abs(v) < MIN_VEL || atEnd || t - began > MAX_FLING) {
            raf = 0;
            endDrag();
            return;
          }
          raf = requestAnimationFrame(tick);
        }
        raf = requestAnimationFrame(tick);
      }

      function onDown(e) {
        // Touch and pen keep their native scrolling. Mouse secondary buttons
        // are for the context menu.
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        eatClick = false;
        if (maxScroll() <= EPS) return;
        stopFling();
        drag = {
          id: e.pointerId,
          startX: e.clientX,
          lastX: e.clientX,
          lastT: e.timeStamp,
          from: vp.scrollLeft,
          moved: false,
          vel: 0
        };
      }

      function onMove(e) {
        if (!drag || e.pointerId !== drag.id) return;
        var dx = e.clientX - drag.startX;

        if (!drag.moved) {
          // Under the slop this is still a click on a product. Do nothing,
          // not even preventDefault, so links and buttons behave normally.
          if (Math.abs(dx) < SLOP) return;
          drag.moved = true;
          // Capture so a throw that leaves the row (or the window) still
          // tracks to its release.
          try { vp.setPointerCapture(drag.id); } catch (err) { void err; }
          root.classList.add('is-dragging');
        }

        e.preventDefault();
        // Only resample speed over a long enough interval. A coalesced pair of
        // moves can arrive microseconds apart, and dividing a real distance by
        // a near-zero dt reports a throw that never happened, which is how a
        // slow, careful drag ends up launching the row.
        var dt = e.timeStamp - drag.lastT;
        if (dt >= VEL_DT) {
          drag.vel = (e.clientX - drag.lastX) / dt;
          drag.lastX = e.clientX;
          drag.lastT = e.timeStamp;
        }
        // Absolute, not incremental: a dropped move event can never make the
        // row drift away from the cursor. A wrap moves the origin with it, so
        // the card stays glued to the pointer straight through the seam.
        vp.scrollLeft = drag.from - dx;
        drag.from += wrapNow();
      }

      function onUp(e) {
        if (!drag || e.pointerId !== drag.id) return;
        var moved = drag.moved;
        var vel = drag.vel;
        try {
          if (vp.hasPointerCapture(drag.id)) vp.releasePointerCapture(drag.id);
        } catch (err) { void err; }
        drag = null;

        if (!moved) return;              // a plain click; let it through
        eatClick = true;                 // it was a throw; swallow the click

        if (DTC.reducedMotion || Math.abs(vel) < MIN_VEL) { endDrag(); return; }
        fling(vel);
      }

      function onCancel(e) {
        if (!drag || e.pointerId !== drag.id) return;
        drag = null;
        endDrag();
      }

      /* ---------------------------------------------------------------- *
       * Wiring
       * ---------------------------------------------------------------- */

      offs.push(DTC.on(prev, 'click', function () { go(-1); }));
      offs.push(DTC.on(next, 'click', function () { go(1); }));
      offs.push(DTC.on(vp, 'scroll', onScroll, { passive: true }));
      offs.push(DTC.on(window, 'resize', schedule, { passive: true }));
      // scrollend is the exact signal; the SETTLE debounce above covers
      // engines that do not have it yet.
      if ('onscrollend' in window) {
        offs.push(DTC.on(vp, 'scrollend', function () {
          if (!drag && !raf) wrapNow();
        }));
      }

      offs.push(DTC.on(vp, 'pointerdown', onDown));
      // Not passive: onMove calls preventDefault once past the slop, to stop
      // the browser starting a text selection mid-throw.
      offs.push(DTC.on(vp, 'pointermove', onMove));
      offs.push(DTC.on(vp, 'pointerup', onUp));
      offs.push(DTC.on(vp, 'pointercancel', onCancel));
      // Links are natively draggable; without this a throw started on a
      // product name becomes a browser link-drag ghost.
      offs.push(DTC.on(vp, 'dragstart', function (e) { e.preventDefault(); }));

      // Capture phase, see the header note. This has to outrun the delegated
      // quick-add handler bound to `document` in dtc-ui.js.
      offs.push(DTC.on(vp, 'click', function (e) {
        if (!eatClick) return;
        eatClick = false;
        e.preventDefault();
        e.stopPropagation();
      }, { capture: true }));

      /* Card widths are clamped against the viewport, so this is what rebuilds
         the loop on a resize, and, more importantly, it is what builds it at
         all when init ran before the row had been laid out.

         It calls sync() DIRECTLY rather than going through schedule(). The
         rAF in schedule() exists to coalesce a burst of scroll events;
         ResizeObserver already delivers at most one callback per frame, so
         routing through it would only make the first build depend on a
         rAF that a background tab never fires. Appending clones changes the
         track's width, not the scroll container's own box, so this cannot
         re-trigger itself. */
      if (window.ResizeObserver) {
        ro = new ResizeObserver(function () {
          if (queued) { cancelAnimationFrame(queued); queued = 0; }
          sync();
        });
        ro.observe(vp);
      }

      build();
      sync();

      return function cleanup() {
        stopFling();
        if (queued) cancelAnimationFrame(queued);
        queued = 0;
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = 0;
        if (drag) {
          try {
            if (vp.hasPointerCapture(drag.id)) vp.releasePointerCapture(drag.id);
          } catch (err) { void err; }
          drag = null;
        }
        offs.forEach(function (off) { off(); });
        offs.length = 0;
        if (ro) { ro.disconnect(); ro = null; }
        // Hand the markup back exactly as Liquid rendered it.
        dropClones();
        vp.scrollLeft = 0;
        root.classList.remove('is-dragging', 'is-scrollable');
        nav.hidden = true;
        prev.disabled = false;
        next.disabled = false;
      };
    }
  });
}());
